import { ReactNode } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import type { Permissions, UserRole } from '@/types/clm';

interface PermissionGateProps {
  children: ReactNode;
  permission?: keyof Permissions;
  role?: UserRole | UserRole[];
  fallback?: ReactNode;
}

/**
 * Component that conditionally renders children based on permissions
 *
 * Usage:
 * <PermissionGate permission="create_contract">
 *   <Button>New Contract</Button>
 * </PermissionGate>
 *
 * <PermissionGate role="admin">
 *   <AdminPanel />
 * </PermissionGate>
 *
 * <PermissionGate role={['admin', 'legal']}>
 *   <AdvancedFeatures />
 * </PermissionGate>
 */
export function PermissionGate({ children, permission, role, fallback = null }: PermissionGateProps) {
  const { can, isRole } = usePermissions();

  // Check permission
  if (permission && !can(permission)) {
    return <>{fallback}</>;
  }

  // Check role
  if (role) {
    const roles = Array.isArray(role) ? role : [role];
    if (!isRole(...roles)) {
      return <>{fallback}</>;
    }
  }

  return <>{children}</>;
}
